import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
    Directive,
    EventEmitter,
    Input,
    isDevMode,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    mixinDisabled,
    mixinInitialized,
} from '@angular/material/core';
import {Subject} from 'rxjs';

/**
 * Возможные виды сортировки
 */
export type CustomSortDirection = 'asc' | 'desc' | '';

/** Интерфейс для директивы, которая содержит состояние сортировки, потребляемое `CustomSortHeader`. */
export interface CustomSortable {
    /** Идентификатор сортируемого столбца. */
    id: string;

    /** Начало сортировки. */
    start: 'asc' | 'desc';

    /** Отключить ли очистку состояния сортировки. */
    disableClear: boolean;
}

/** Текущее состояние сортировки. */
export interface Sort {
    /** Идентификатор сортируемого столбца. */
    active: string;

    /** Направление сортировки. */
    direction: CustomSortDirection;
}

// Класс для применения миксинов в CustomSort.
export class CustomSortBase {
}

export const _CustomSortMixinBase:any =
    mixinInitialized(mixinDisabled(CustomSortBase));

/** Контейнер для CustomSortables для управления состоянием сортировки и предоставления параметров сортировки по умолчанию. */
@Directive({
    selector: '[customSort]',
    exportAs: 'customSort',
    inputs: ['disabled: customSortDisabled']
})
export class CustomSort extends _CustomSortMixinBase
    implements  OnChanges, OnDestroy, OnInit {
    /** Сбор всех зарегистрированных сортируемых материалов, которыми управляет эта директива. */
    sortables = new Map<string, CustomSortable>();

    /** Используется для уведомления любых дочерних компонентов, слушающих изменения состояния. */
    readonly _stateChanges = new Subject<void>();

    /** Идентификатор последней отсортированной CustomSortable. */
    @Input('customSortActive') active: { name: string, sort: CustomSortDirection }[] = [];

    /**
     * Направление, которое нужно указать при первоначальной сортировке CustomSortable.
     * Может быть переопределено началом сортировки CustomSortable.
     */
    @Input('customSortStart') start: 'asc' | 'desc' = 'asc';

    /** Направление сортировки текущего активного CustomSortable. */
    getDirection(id: string): CustomSortDirection {
        let direction: CustomSortDirection = '';
        this.active.forEach(active => {
            if (active.name == id) {
                direction = active.sort;
            }
        });
        return direction;
    }

    @Input('customSortDirection')
    setDirection(id: string, direction: CustomSortDirection) {
        if (isDevMode() && direction && direction !== 'asc' && direction !== 'desc') {
            throw Error(`${direction} is not a valid sort direction ('asc' or 'desc').`);
        }
        this.active.forEach(active => {
            if (active.name == id) {
                active.sort = direction;
            }
        });
    }

    /**
     * Отключить ли пользователю очистку сортировки, завершив цикл направления сортировки.
     * Может быть отменено отключением очистки ввода в CustomSortable.
     */
    @Input('customSortDisableClear')
    get disableClear(): boolean {
        return this._disableClear;
    }

    set disableClear(v: boolean) {
        this._disableClear = coerceBooleanProperty(v);
    }

    private _disableClear: boolean;

    /** Событие, генерируемое, когда пользователь меняет либо активную сортировку, либо направление сортировки. */
    @Output('customSortChange') readonly sortChange: EventEmitter<Sort[]> = new EventEmitter<Sort[]>();

    /**
     * Регистрация функции, которая будет использоваться содержащимися CustomSortables. Добавляет CustomSortable к
     * коллекции CustomSortables.
     */
    register(sortable: CustomSortable): void {
        if (!sortable.id) {
            throw Error(`CustomSortHeader must be provided with a unique id.`);
        }

        if (this.sortables.has(sortable.id)) {
            throw Error(`Cannot have two CustomSortables with the same id (${sortable.id}).`);
        }
        this.sortables.set(sortable.id, sortable);
    }

    /**
     * Отменить регистрацию функции, которая будет использоваться содержащимися CustomSortables. Удаляет CustomSortable из
     * коллекции содержимого CustomSortables.
     */
    deregister(sortable: CustomSortable): void {
        this.sortables.delete(sortable.id);
    }

    /** Устанавливает идентификатор активной сортировки и определяет новое направление сортировки. */
    sort(sortable: CustomSortable, multy: boolean): void {
        let is_active = false;
        let currentActive: { name: string, sort: CustomSortDirection } = {name: '', sort: ''};
        this.active.forEach(active => {
            if (active.name == sortable.id) {
                is_active = true;
                currentActive = active;
            }
        });
        if (!is_active) {
            if (multy) {
                this.active.push({name: sortable.id, sort: sortable.start ? sortable.start : this.start});
            } else {
                this.active = [{name: sortable.id, sort: sortable.start ? sortable.start : this.start}];
            }
        } else {
            if (multy) {
                this.setDirection(sortable.id, this.getNextSortDirection(sortable));
            } else {
                this.active = [currentActive];
                this.setDirection(sortable.id, this.getNextSortDirection(sortable));
            }
        }
        let sortChange = [];
        if (multy) {
            this.active.forEach(active => {
                sortChange.push({
                    active: active.name, direction: active.sort
                });
            });
        } else {
            sortChange = [{active: sortable.id, direction: this.getDirection(sortable.id)}];
        }
        this.sortChange.emit(sortChange);
    }

    /** Возвращает следующее направление сортировки активного сортируемого, проверяя возможные переопределения. */
    getNextSortDirection(sortable: CustomSortable): CustomSortDirection {
        if (!sortable) {
            return '';
        }

        // Получить цикл направления сортировки с потенциальными сортируемыми переопределениями.
        const disableClear = sortable.disableClear != null ? sortable.disableClear : this.disableClear;
        let sortDirectionCycle = getSortDirectionCycle(sortable.start || this.start, disableClear);

        // Получить и вернуть следующее направление в цикле
        let nextDirectionIndex = sortDirectionCycle.indexOf(this.getDirection(sortable.id)) + 1;
        if (nextDirectionIndex >= sortDirectionCycle.length) {
            nextDirectionIndex = 0;
        }
        return sortDirectionCycle[nextDirectionIndex];
    }

    ngOnInit() {
        this._markInitialized();
    }

    ngOnChanges() {
        this._stateChanges.next();
    }

    ngOnDestroy() {
        this._stateChanges.complete();
    }
}

/** Возвращает цикл направления сортировки, чтобы использовать предоставленные параметры порядка и очистки. */
function getSortDirectionCycle(start: 'asc' | 'desc',
                               disableClear: boolean): CustomSortDirection[] {
    let sortOrder: CustomSortDirection[] = ['asc', 'desc'];
    if (start == 'desc') {
        sortOrder.reverse();
    }
    if (!disableClear) {
        sortOrder.push('');
    }

    return sortOrder;
}
